import { useState } from "react";
import { visionService } from "@/services/vision-service";
import { reportError } from "@/utils/observability";
import { parseMoney } from "@/utils/parse-money";
import type {
  DiagnosisData,
  NewSearchTolerance,
  RankedSpace,
  SearchAndRankBody,
  SearchSource,
} from "../types";
import type {
  SearchParams,
  SearchFilters,
} from "@/services/private-office-service/types";

type SearchAndRankStatus = "idle" | "loading" | "success" | "error";

export interface SearchModeState {
  mode: "location" | "neighborhood";
  location?: { latitude: number; longitude: number; radius: number };
  neighborhoods?: string[];
  cityName?: string;
  minCapacity?: number | null;
  maxCapacity?: number | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  unitCreditPrice?: number;
  includeUnavailableWithinPeriod?: number;
  /** Só espaços com AVCB vigente (PED-14095) */
  requireAvcb?: boolean;
  /** Só espaços com alvará de funcionamento vigente (PED-14095) */
  requirePermit?: boolean;
}

export function buildSearchModeParams(
  state: SearchModeState,
  tolerance?: NewSearchTolerance,
): SearchParams {
  const filters: SearchFilters = {
    minCapacity: state.minCapacity ?? undefined,
    maxCapacity: state.maxCapacity ?? undefined,
    minPrice: state.minPrice ?? null,
    maxPrice: state.maxPrice ?? null,
    status: [0],
    approvalStatus: "approved",
    unitCreditPrice: state.unitCreditPrice ?? 80,
    skipRecentUpdatesFilter: false,
    includeRoomCombinations: (state.minCapacity ?? 0) > 0,
    ...(state.includeUnavailableWithinPeriod != null && {
      includeUnavailableWithinPeriod: state.includeUnavailableWithinPeriod,
    }),
    ...(state.requireAvcb && { hasAvcb: true }),
    ...(state.requirePermit && { hasPermit: true }),
  };

  return {
    mode: state.mode,
    ...(state.mode === "location"
      ? { location: state.location }
      : {
          neighborhoods: state.neighborhoods,
          ...(state.cityName && { cityName: state.cityName }),
        }),
    filters,
    tolerance,
  };
}

export interface RoomCombinationRoom {
  privateRoomId: string;
  label: string;
  capacity: number;
  monthlyPrice: number;
  sizeM2?: number | null;
  amenitySlugs?: string[];
  photos?: string[];
}

export interface RoomCombination {
  combinationId: string;
  spaceId: string;
  spaceName: string;
  neighborhood: string;
  distanceKm: number;
  spacePhotos?: string[];
  spaceAddress?: string;
  rooms: RoomCombinationRoom[];
  totalCapacity: number;
  totalMonthlyPrice: number;
  score: number;
}

interface UseSearchAndRankReturn {
  spaces: RankedSpace[];
  extraSpaces: RankedSpace[];
  outdatedSpaces: RankedSpace[];
  roomCombinations: RoomCombination[];
  status: SearchAndRankStatus;
  recommendationHash: string | null;
  searchAndRank: (
    body: SearchAndRankBody,
    options?: {
      searchSource?: SearchSource | null;
      includeUnavailableWithinPeriod?: number;
    },
  ) => Promise<{
    spaces: RankedSpace[];
    outdatedSpaces: RankedSpace[];
    recommendationHash: string | null;
  }>;
  searchMode: (params: SearchParams) => Promise<{
    spaces: RankedSpace[];
    extraSpaces: RankedSpace[];
    outdatedSpaces: RankedSpace[];
    recommendationHash: string | null;
  }>;
}

export interface SearchAndRankOverrides {
  city?: string;
  min_capacity?: number | null;
  max_capacity?: number | null;
  min_budget?: number | null;
  max_budget?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  radius_km?: number | null;
  unit_credit_price?: number | null;
  preferred_neighborhoods?: string[];
}

export function buildSearchAndRankBody(
  diagnosis: DiagnosisData,
  overrides?: SearchAndRankOverrides,
): SearchAndRankBody {
  const imported = diagnosis.importedData;
  const budget = parseMoney(diagnosis.monthlyBudget);

  const baseConstraints = imported?.hard_constraints ?? {
    city: diagnosis.targetCity,
    min_capacity:
      diagnosis.positionsMin !== "" ? Number(diagnosis.positionsMin) : null,
    max_capacity:
      diagnosis.positionsMax !== "" ? Number(diagnosis.positionsMax) : null,
    max_budget: budget,
    mandatory_amenities: [],
  };

  const diagnosisMinCapacity =
    diagnosis.positionsMin !== "" && diagnosis.positionsMin !== undefined
      ? Number(diagnosis.positionsMin)
      : undefined;
  const diagnosisMaxCapacity =
    diagnosis.positionsMax !== "" && diagnosis.positionsMax !== undefined
      ? Number(diagnosis.positionsMax)
      : undefined;

  const effectiveCity =
    overrides?.city?.trim() ||
    diagnosis.targetCity?.trim() ||
    baseConstraints.city;

  return {
    hard_constraints: {
      ...baseConstraints,
      city: effectiveCity,
      ...(diagnosisMinCapacity !== undefined && {
        min_capacity: diagnosisMinCapacity,
      }),
      ...(diagnosisMaxCapacity !== undefined && {
        max_capacity: diagnosisMaxCapacity,
      }),
      ...(overrides?.min_capacity !== undefined && {
        min_capacity: overrides.min_capacity,
      }),
      ...(overrides?.max_capacity !== undefined && {
        max_capacity: overrides.max_capacity,
      }),
      ...(overrides?.min_budget !== undefined && {
        min_budget: overrides.min_budget,
      }),
      ...(overrides?.max_budget !== undefined && {
        max_budget: overrides.max_budget,
      }),
      ...(overrides?.latitude !== undefined && {
        latitude: overrides.latitude,
      }),
      ...(overrides?.longitude !== undefined && {
        longitude: overrides.longitude,
      }),
      ...(overrides?.radius_km !== undefined && {
        radius_km: overrides.radius_km,
      }),
      ...(overrides?.unit_credit_price !== undefined &&
        overrides?.unit_credit_price !== null && {
          unit_credit_price: overrides.unit_credit_price,
        }),
    },
    soft_preferences: {
      ...(imported?.soft_preferences ?? {
        preferred_neighborhoods: [],
        nice_to_have_amenities: [],
        mobility_needs: [],
        infrastructure_needs: [],
      }),
      ...(overrides?.preferred_neighborhoods !== undefined && {
        preferred_neighborhoods: overrides.preferred_neighborhoods,
      }),
    },
    cultural_fit: imported?.cultural_fit ?? {
      company_vibe: "",
      work_model: null,
      deal_breakers: [],
    },
    summary_narrative: imported?.summary_narrative ?? diagnosis.needs ?? "",
    confidence: imported?.confidence ?? 0,
    company_name: diagnosis.companyName,
    deal_context: {
      deal_id: diagnosis.dealId,
      deal_name: "",
      deal_amount: 0,
      deal_city: effectiveCity,
      deal_state: "",
      deal_neighborhood: "",
      deal_positions_demand:
        diagnosis.positionsMin !== "" || diagnosis.positionsMax !== ""
          ? `${diagnosis.positionsMin ?? ""}–${diagnosis.positionsMax ?? ""}`
          : "",
      deal_rooms_quantity: "",
      deal_allocated_budget: diagnosis.monthlyBudget ?? "",
      company_name: diagnosis.companyName,
      company_industry: diagnosis.segment ?? "",
      company_size: diagnosis.numberOfEmployees ?? 0,
      numberofemployees: diagnosis.numberOfEmployees ?? null,
      company_domain: "",
      contact_name: diagnosis.contactName,
      contact_email: diagnosis.contactEmail,
      contact_title: "",
      engagements: [],
    },
    ...(overrides?.unit_credit_price !== undefined &&
      overrides?.unit_credit_price !== null && {
        unit_credit_price: overrides.unit_credit_price,
      }),
    ...(budget !== null && { current_monthly_cost: budget }),
  };
}

export function useSearchAndRank(): UseSearchAndRankReturn {
  const [spaces, setSpaces] = useState<RankedSpace[]>([]);
  const [extraSpaces, setExtraSpaces] = useState<RankedSpace[]>([]);
  const [outdatedSpaces, setOutdatedSpaces] = useState<RankedSpace[]>([]);
  const [roomCombinations, setRoomCombinations] = useState<RoomCombination[]>(
    [],
  );
  const [status, setStatus] = useState<SearchAndRankStatus>("idle");
  const [recommendationHash, setRecommendationHash] = useState<string | null>(
    null,
  );

  async function searchAndRank(
    body: SearchAndRankBody,
    options?: {
      searchSource?: SearchSource | null;
      includeUnavailableWithinPeriod?: number;
    },
  ): Promise<{
    spaces: RankedSpace[];
    outdatedSpaces: RankedSpace[];
    recommendationHash: string | null;
  }> {
    setStatus("loading");
    try {
      const result = await visionService.searchAndRank(body, options);
      setSpaces(result.spaces);
      setOutdatedSpaces(result.outdatedSpaces);
      setRecommendationHash(result.recommendationHash);
      setStatus("success");
      return result;
    } catch (error) {
      reportError(error, {
        operation: "searchAndRank",
        step: "location",
        dealId: body.deal_context?.deal_id,
      });
      setSpaces([]);
      setOutdatedSpaces([]);
      setRecommendationHash(null);
      setStatus("error");
      return { spaces: [], outdatedSpaces: [], recommendationHash: null };
    }
  }

  async function searchMode(params: SearchParams): Promise<{
    spaces: RankedSpace[];
    extraSpaces: RankedSpace[];
    outdatedSpaces: RankedSpace[];
    recommendationHash: string | null;
  }> {
    setStatus("loading");
    try {
      const result = await visionService.searchMode(params);
      setSpaces(result.spaces);
      setExtraSpaces(result.extraSpaces ?? []);
      setOutdatedSpaces(result.outdatedSpaces);
      setRoomCombinations(result.roomCombinations ?? []);
      setRecommendationHash(result.recommendationHash);
      setStatus("success");
      return result;
    } catch (error) {
      reportError(error, { operation: "searchMode", step: "location" });
      setSpaces([]);
      setExtraSpaces([]);
      setOutdatedSpaces([]);
      setRoomCombinations([]);
      setRecommendationHash(null);
      setStatus("error");
      return {
        spaces: [],
        extraSpaces: [],
        outdatedSpaces: [],
        recommendationHash: null,
      };
    }
  }

  return {
    spaces,
    extraSpaces,
    outdatedSpaces,
    roomCombinations,
    status,
    recommendationHash,
    searchAndRank,
    searchMode,
  };
}
